// Cost reports (docs/12 "Execution as built", Costs): what the Costs page and a task group's cost
// card derive from `{period, since, total_usd, …, groups, tasks, users, days}`: the daily series
// with the days nothing ran filled in, and who paid.
import type { Schemas } from "../api/client";
import { formatCompact, formatUsd, messages } from "../i18n";

export type CostReport = Schemas["CostReportDto"];
export type CostLine = Schemas["CostLineDto"];

export const PERIODS = ["day", "week", "month", "all"] as const;
export type Period = (typeof PERIODS)[number];

export const parsePeriod = (x: string | null): Period => PERIODS.find((p) => p === x) ?? "month";

const DAY = 86_400_000;
const utcMidnight = (ms: number) => Math.floor(ms / DAY) * DAY;

export interface DayPoint {
  /** UTC midnight. */
  start: number;
  org: number;
  platform: number;
}

/**
 * One point per UTC day from the period's first day (or the first day anything was spent, for
 * `all`) to today, zero where the report has no line. Empty when nothing was spent at all.
 */
export function dailySeries(report: CostReport, now: number): DayPoint[] {
  const byDay = new Map(report.days.flatMap((d) => (d.start == null ? [] : [[utcMidnight(d.start), d] as const])));
  const starts = [...byDay.keys()];
  if (starts.length === 0) return [];
  const first = report.since == null ? Math.min(...starts) : utcMidnight(report.since);
  const last = Math.max(utcMidnight(now), ...starts);
  const points: DayPoint[] = [];
  for (let day = first; day <= last; day += DAY) {
    const line = byDay.get(day);
    points.push({ start: day, org: line?.org_cost_usd ?? 0, platform: line?.platform_cost_usd ?? 0 });
  }
  return points;
}

/** A UTC day as a local date of the same calendar day, for labels ("12 Sep" everywhere). */
export function calendarDay(utcMidnightMs: number): Date {
  const d = new Date(utcMidnightMs);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export interface PaidSplit {
  org: number;
  platform: number;
  /** The platform's share of the total, 0–100; 0 when nothing was spent. */
  platformPct: number;
}

export function paidSplit(r: Pick<CostReport, "org_cost_usd" | "platform_cost_usd">): PaidSplit {
  const total = r.org_cost_usd + r.platform_cost_usd;
  return { org: r.org_cost_usd, platform: r.platform_cost_usd, platformPct: total > 0 ? (r.platform_cost_usd / total) * 100 : 0 };
}

export const tokens = (r: Pick<CostLine, "input_tokens" | "output_tokens">): number => r.input_tokens + r.output_tokens;

/** "$1.23", "<$0.01" for a cost too small to show, "$0.00" for nothing; in the active language. */
export function money(n: number): string {
  return n > 0 && n < 0.01 ? messages().costs.lessThan(formatUsd(0.01)) : formatUsd(n);
}

/** 1250000 → "1.3m", 45210 → "45.2k", 980 → "980" (Intl's compact notation). */
export const compact = formatCompact;
